<template>
  <div class="filter-container">
    <FilterList
      isPotential
      isTalentPool
      ref="filterListRef"
      class="talent-pool-list"
      hiddenCandidateNameSearch
      hiddenCandidateIdSearch
      hiddenCurrentSelectedCount
      hiddenMyCandidateSwitch
      hiddenEmployeeIdSearch
      :candidateList="candidateList"
      :listLoading="listLoading"
      :searchTemplate.sync="searchTemplate"
      :searchTemplateList="searchTemplateList"
      :filterGroupList="filterList"
      :candidateTotalCount="candidateTotalCount"
      :jobName="jobName"
      :hasSoftUserPermission="hasSoftUserPermission"
      @search="handelSearch"
      @scrollBottom="handleScrollBottom"
      @openDetail="handleOpenDetail"
      @createSearchTemplate="handleCreateSearchTemplate"
      @updateSearchTemplate="handleUpdateSearchTemplate"
      @clearSearchTemplate="handleClearSearchTemplate"
      @deleteSearchTemplate="handleDeleteSearchTemplate"
    >
      <template #tableContainer>
        <TableList
          ref="tableContainer"
          :data="candidateList"
          :loading="listLoading"
          :fromJobPotential="fromJobPotential"
          @jobChange="handleJobChange"
          @scrollBottom="handleScrollBottom"
          @openDetail="handleOpenDetail"
        ></TableList>
      </template>
    </FilterList>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, createI18nProperty, getI18nPropertyValue } from '@/i18n'
import FilterList from '../applicantList/filterList/layout.vue'
import TagSelect from '../applicantList/tagSelect.vue'
import TableList from './potentialCandidateList.vue'
import JobSelector from '../components/jobSelector.vue'

export default {
  components: {
    FilterList,
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      pagination: {
        page: 1,
        pageSize: 20,
        totalPage: 2
      },
      searchTemplate: null,
      searchTemplateList: [],
      savedFilterList: [],
      candidateList: [],
      listLoadingCount: 0,
      isSelectedAllCandidate: false,
      selectedCandidateList: [],
      isSavingSearchTemplate: false,
      candidateTotalCount: 0,
      searchedGroupList: [],
      searchParams: null,
      jobName: null,
      search: {}
    }
  },
  computed: {
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    },
    hasMicrositeCampaignPermission () {
      return this.hasAnyPermission('PREMIUM_MICROSITE_CAMPAIGN_MANAGEMENT')
    },
    hasSoftUserPermission () {
      return this.hasAnyPermission('PREMIUM_SOFT_USER')
    },
    jobId () {
      return this.$route.params.jobId
    },
    talentFilterGroupId () {
      return this.$route.params.talentFilterGroupId
    },
    fromJobPotential () {
      if (this.$route.params.jobId) {
        return true
      } else {
        return false
      }
    },
    listLoading: {
      get () {
        return this.listLoadingCount > 0
      },
      set (val) {
        if (val) {
          this.listLoadingCount += 1
        } else {
          this.listLoadingCount -= 1
        }
      }
    },
    categoryList () {
      const categoryList = ['manual', 'smart', 'system']
      if (this.hasMicrositeCampaignPermission) {
        categoryList.push('campaign')
      }
      return categoryList
    },
    filterList () {
      const list = [
        {
          title: this.$t(`${this.i18nPrefix}.label.tag`),
          filterList: {
            tag: {
              elType: TagSelect,
              multiple: true,
              hidden: !this.hasCRMTagPermission,
              props: {
                categoryList: this.categoryList
              },
              valueDisplay: {
                formatter: (value, filter, filterName, group, groupKey) => {
                  if (!value?.selected || value.selected.length < 1) {
                    return
                  }
                  return _.map(value.selected, (item, index) => {
                    return {
                      valueKey: index,
                      value: item,
                      detail: `${group.title} - ${filter.label}: ${item.name}`,
                      summary: `${item.name}`
                    }
                  })
                }
              },
              cancelSelected: (filterValue, value) => {
                filterValue.selected = _.filter(filterValue.selected, item => item.id !== value.id)
                return filterValue
              },
              setParam: (params, val) => {
                params.tag_id = _.map(val.selected, 'id').join(',')
                params.is_match_all_tag = val.isMatchAll ? '1' : '0'
              }
            }
          }
        },
        {
          title: this.$t(`${this.i18nPrefix}.label.job`),
          filterList: {
            appliedJob: {
              elType: JobSelector,
              hidden: this.fromJobPotential,
              label: this.$t(`${this.i18nPrefix}.label.job`),
              multiple: true,
              valueDisplay: {
                formatter: (value, filter, filterName, group, groupKey) => {
                  if (!value || value.length < 1) {
                    return
                  }
                  return _.map(value, (item, index) => {
                    return {
                      valueKey: item.id,
                      value: item.id,
                      detail: `${group.title} - ${filter.label}: ${item.i18nName}`,
                      summary: `${item.i18nName}`
                    }
                  })
                }
              },
              cancelSelected: (filterValue, value) => {
                filterValue = _.filter(filterValue, item => item.id !== value)
                return filterValue
              },
              setParam: (params, val) => {
                const jobIds = []
                const jobTypes = []
                _.forEach(val, job => {
                  if (job.type === 'MY_JOB') {
                    jobTypes.push(job.id)
                  } else {
                    jobIds.push(job.id)
                  }
                })
                params.job_id = jobIds.join(',')
                params.job_types = jobTypes.join(',')
              }
            }
          }
        }
      ]
      return list
    }
  },
  created () {
    if (!this.hasSoftUserPermission) {
      this.$router.push('error/no-permission')
    }
    this.initPageData()
  },
  methods: {
    ...mapActions('company', ['getCompanyGroupedJobList', 'getCompanyChildrenTree']),
    ...mapActions('account', [
      'getAccountPageConfigList',
      'createAccountPageConfig',
      'updateAccountPageConfig',
      'deleteAccountPageConfig'
    ]),
    ...mapActions('job', ['getJobInfo']),
    ...mapActions('potential', ['getCompanyPotentialCandidateList', 'getCandidateReferralChain']),
    async loadJobInfo () {
      if (!this.jobId) {
        return
      }
      const resp = await this.getJobInfo({ jobId: this.jobId })
      if (resp) {
        const job = resp.job
        addI18nProperty(job, 'jobName', 'cn_name', 'name')
        this.jobName = job.jobName
      }
    },
    async initPageData () {
      this.$eventBus.$emit('showLoading', true)
      Promise.all([this.loadJobInfo(), this.loadSearchTemplateList()]).finally(() => this.$eventBus.$emit('showLoading', false))
      this.handelSearch()
    },
    async loadAppliedJobList () {
      const resp = await this.getCompanyGroupedJobList()
      if (resp) {
        const companyJobList = _.map(resp, company => {
          addI18nProperty(company, 'i18nName', 'cn_name', 'name')
          company.job_list = _.map(company.job_list, job => {
            job.cn_title = `${job.id} - ${job.cn_title}`
            job.title = `${job.id} - ${job.title}`
            addI18nProperty(job, 'i18nName', 'cn_title', 'title')
            return job
          })
          return company
        })
        companyJobList.splice(0, 0, createI18nProperty({
          type: 'MY_JOB',
          job_list: [createI18nProperty({ id: 'MY_JOB', type: 'MY_JOB' }, 'i18nName', `${this.i18nPrefix}.label.myJob`)]
        }, 'i18nName', `${this.i18nPrefix}.label.myJob`))
        this.appliedJobList = companyJobList
      }
      return this.appliedJobList
    },
    async loadSearchTemplateList () {
      this.$eventBus.$emit('showLoading', true)
      let pageType = 'TALENT_POOL_POTENTIAL'
      if (this.fromJobPotential) {
        pageType = 'JOB_APPLICATION_POTENTIAL'
      }
      try {
        const resp = await this.getAccountPageConfigList({ page_type: pageType })
        if (resp) {
          this.searchTemplateList = _.map(resp, pageConfig => {
            return {
              _config: pageConfig,
              id: pageConfig.id,
              name: pageConfig.config.name,
              search: pageConfig.config.search
            }
          })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleCreateSearchTemplate (template, callback) {
      if (this.isSavingSearchTemplate) {
        return
      }
      this.isSavingSearchTemplate = true
      this.$eventBus.$emit('showLoading', true)
      let pageType = 'TALENT_POOL_POTENTIAL'
      if (this.fromJobPotential) {
        pageType = 'JOB_APPLICATION_POTENTIAL'
      }
      try {
        const resp = await this.createAccountPageConfig({
          page_type: pageType,
          config: {
            name: template.name,
            search: template.search
          }
        })
        if (resp) {
          const templateInfo = {
            _config: resp,
            id: resp.id,
            name: resp.config.name,
            search: resp.config.search
          }
          callback(templateInfo)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
        this.isSavingSearchTemplate = false
      }
    },
    async handleUpdateSearchTemplate (template, callback) {
      if (this.isSavingSearchTemplate) {
        return
      }
      this.isSavingSearchTemplate = true
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.updateAccountPageConfig({
          id: template._config.id,
          config: {
            name: template.name,
            search: template.search
          }
        })
        if (resp) {
          const templateInfo = {
            _config: resp,
            id: resp.id,
            name: resp.config.name,
            search: resp.config.search
          }
          callback(templateInfo)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
        this.isSavingSearchTemplate = false
      }
    },
    async handleClearSearchTemplate () {
      this.searchTemplate = null
    },
    async handleDeleteSearchTemplate (searchTemplate, deletedTemplateIndex) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.deleteAccountPageConfig({
          id: searchTemplate.id
        })
        if (resp) {
          this.searchTemplateList.splice(deletedTemplateIndex, 1)
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.deleteSuccess`)
          })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    parseSearchToParams (search) {
      const params = {}
      const filterList = _.filter(this.filterList, item => !item.hidden)
      _.forEach(search, (group, groupKey) => {
        if (groupKey === 'keywords') {
          params.keywords = group
          return
        }
        const groupFilters = filterList[groupKey]
        _.forEach(group, (filterValue, filterName) => {
          const filter = groupFilters?.filterList[filterName]
          if (!filter || !filterValue) {
            return
          }
          filter.setParam(params, filterValue)
        })
      })
      return params
    },
    async handelSearch (search, nextPage, targetPage) {
      if (nextPage) {
        if (this.pagination.page === this.pagination.totalPage) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.noData`))
          return
        }
        this.pagination.page += 1
      } else {
        this.pagination.page = 1
      }
      this.listLoading = true
      if (!search) {
        search = this.search
      } else {
        this.search = search
      }
      const params = this.parseSearchToParams(search)
      this.searchParams = params
      if (this.searchedGroupList.length > 0) {
        params.groups = JSON.stringify(this.searchedGroupList)
      }
      const page = targetPage || this.pagination.page
      const candidateList = []
      const promiseList = _.chain(this.pagination.page).range(page + 1)
        .map((val) => this.getCompanyPotentialCandidateList({
          ...params,
          is_talent_pool: !this.fromJobPotential,
          current_job_id: this.jobId,
          page: val,
          page_size: this.pagination.pageSize
        })).value()
      const respList = await Promise.all(promiseList)
      let totalPage = this.pagination.totalPage
      let candidateTotalCount = this.candidateTotalCount
      _.forEach(respList, (resp) => {
        if (resp) {
          totalPage = resp.total_page
          candidateTotalCount = resp.total_count
          _.forEach(resp.data, potential => {
            const candidate = potential.candidate
            const jobListLength = potential.job_info_list.length
            potential.job = jobListLength ? potential.job_info_list[0].id : null
            potential.referral_chain = _.reverse(potential.referral_chain)
            _.forEach(potential.job_info_list, job => {
              addI18nProperty(job, 'i18nName', 'cn_name', 'en_name')
            })
            Object.defineProperty(candidate, 'locationInfo', {
              configurable: true,
              enumerable: true,
              get () {
                const location = candidate.location
                if (!location) {
                  return null
                }
                const city = location.city
                const province = location.province
                const country = location.country
                return getI18nPropertyValue(city || province || country, 'cn_name', 'name')
              }
            })
          })
          candidateList.push(...resp.data)
        }
      })
      if (this.pagination.page === 1) {
        this.candidateList = candidateList
      } else {
        this.candidateList.push(...candidateList)
      }
      this.pagination.page = page
      this.candidateTotalCount = candidateTotalCount
      this.pagination.totalPage = totalPage
      this.listLoading = false
    },
    handleScrollBottom (search) {
      this.handelSearch(search, true)
    },
    handleOpenDetail (rowData) {
      const candidateId = rowData.candidate.id
      const potentialId = rowData.id
      const params = encodeURIComponent(JSON.stringify(this.parseSearchToParams(this.search)))
      const backUrl = encodeURIComponent(this.$route.fullPath)
      if (this.fromJobPotential) {
        window.open(`/v-admin/job/${this.jobId}/candidate/${potentialId}/potential-profile?candidate_id=${candidateId}&list_params=${params}&back=${backUrl}`)
      } else {
        window.open(`/v-admin/talent-pool/${this.talentFilterGroupId}/candidate/${potentialId}/potential-profile?candidate_id=${candidateId}&list_params=${params}&back=${backUrl}`)
      }
    },
    async handleJobChange (rowData) {
      const candidateId = rowData.candidate.id
      const jobId = rowData.job
      if (!candidateId || !jobId) {
        return
      }
      const params = {
        candidate_id: candidateId,
        job_id: jobId
      }
      const resp = await this.getCandidateReferralChain(params)
      if (resp) {
        _.reverse(resp.referral_chain)
        rowData.referral_chain = resp.referral_chain
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  width: 100%;
  height: 100%;

  .talent-pool-list {

  }

  .rate-dialog {
    ::v-deep {
      .el-dialog {
        margin: auto;

        .el-dialog__body {
          padding: 30px 30px;
        }
      }
    }
  }

  .interview-dialog {
    ::v-deep {
      .el-dialog {
        margin: auto;

        .el-dialog__body {
          padding: 30px 30px;
        }
      }
    }
  }

  .change-candidate-status-dialog {
    ::v-deep {
      .el-dialog__header {
        padding: 34px 40px 0px;
      }

      .el-dialog__body {
        padding: 0px 40px 16px;
      }

      .el-dialog__title {
        font-size: 22px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
}
</style>
<style lang="scss">
.school-popper, .applied-job-popper, .consent-info-popper {
  max-width: 300px;

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.referrer-info-popper {
  background: #e6e6e6 !important;
  .popper__arrow {
    &::after {
      border-bottom-color: #e6e6e6 !important;
    }
  }
}
</style>
